import React, { useState } from 'react';
import {
  Settings,
  AlertCircle,
  CheckCircle,
  Wrench,
  RefreshCw,
  Info
} from 'lucide-react';
import { batchFixMissingCurrentStep } from '../services/adminService';

const AdminSettings: React.FC = () => {
  const [fixStatus, setFixStatus] = useState<'idle' | 'fixing' | 'fixed' | 'error'>('idle');
  const [fixStats, setFixStats] = useState<{ fixed: number; skipped: number; errors: number } | null>(null);

  const handleFixMissingCurrentStep = async () => {
    if (!window.confirm('This will scan all sessions and fix any missing currentStep fields. Continue?')) {
      return;
    }

    setFixStatus('fixing');
    setFixStats(null);

    try {
      const stats = await batchFixMissingCurrentStep();
      setFixStats(stats);
      setFixStatus('fixed');
      setTimeout(() => setFixStatus('idle'), 5000);
    } catch (error) {
      console.error('Error fixing currentStep:', error);
      setFixStatus('error');
      setTimeout(() => setFixStatus('idle'), 5000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings & Maintenance</h2>
        <p className="text-gray-600 mt-1">
          System maintenance tools and configuration
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Settings Panel</p>
            <p>This section currently contains maintenance tools. Additional system settings and configuration options will be added as needed.</p>
          </div>
        </div>
      </div>

      {/* Maintenance Tools */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <Wrench className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Maintenance Tools</h3>
        </div>

        <div className="space-y-6">
          {/* Fix Missing Current Step */}
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Fix Missing Current Step</h4>
            <p className="text-sm text-gray-600 mb-3">
              Scans all calculator sessions in the database and fixes any missing currentStep fields. This tool analyzes session data to determine the correct step based on completed fields and saved results.
            </p>
            <div className="flex items-start gap-3">
              <button
                onClick={handleFixMissingCurrentStep}
                disabled={fixStatus === 'fixing'}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${fixStatus === 'fixing' ? 'animate-spin' : ''}`} />
                {fixStatus === 'fixing' ? 'Scanning & Fixing...' : 'Run Fix'}
              </button>
              <div className="text-xs text-gray-500 mt-2">
                Safe to run multiple times
              </div>
            </div>

            {fixStatus === 'fixed' && fixStats && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Fix completed successfully</span>
                </div>
                <div className="text-sm text-green-600 space-y-1">
                  <p>• Fixed: {fixStats.fixed} sessions</p>
                  <p>• Skipped: {fixStats.skipped} sessions (already had currentStep)</p>
                  {fixStats.errors > 0 && <p>• Errors: {fixStats.errors} sessions</p>}
                </div>
              </div>
            )}

            {fixStatus === 'error' && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">Error running fix</span>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  Check the browser console for error details.
                </p>
              </div>
            )}
          </div>

          {/* Placeholder for future tools */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 italic">
              More maintenance tools will be added here as needed (e.g., data cleanup, session archival, report regeneration).
            </p>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <Settings className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">System Information</h3>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Database</p>
            <p className="font-medium">Firebase Firestore</p>
          </div>
          <div>
            <p className="text-gray-600">Authentication</p>
            <p className="font-medium">Session-based Admin Access</p>
          </div>
          <div>
            <p className="text-gray-600">Hosting</p>
            <p className="font-medium">Firebase Hosting</p>
          </div>
          <div>
            <p className="text-gray-600">Report Storage</p>
            <p className="font-medium">Firestore Documents</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;